import { coerceTimeFormat } from "@app/_helpers/utils";
import { ComegoTime, ComegoTimeType, Time } from "@timeghost/web-api";
import { addMilliseconds, addSeconds, endOfDay, startOfDay } from "date-fns/esm";

type ProjectTimeInterval = { start: Date; end: Date };
export type ScheduleWorkPeriod = { from: string; to: string };
/** Break rule as stored on the workspace: both fields are decimal hours (minutesDue 0.5 = 30 min). */
export type BreakRule = { workingHours: number; minutesDue: number };

const DEFAULT_SCHEDULE_WORK_PERIODS: ScheduleWorkPeriod[] = [
	{ from: "08:00", to: "12:00" },
	{ from: "13:00", to: "17:00" },
];

const HOUR_MS = 3600 * 1000;
/** Bounds the incremental placement loop so a degenerate rule config cannot spin it forever. */
const MAX_PLACEMENT_STEPS = 64;

/** Fields carried over when a row is re-created verbatim instead of derived (absence name/type). */
type WorkingHourEntrySource = { name?: string | null; typeId?: string };

export type DesiredWorkingHourEntry = {
	start: Date;
	end: Date;
	durationMs: number;
	type: ComegoTimeType;
	source?: WorkingHourEntrySource;
};

export type WorkingHourChangeSet = {
	create: DesiredWorkingHourEntry[];
	update: Array<{ id: string; start: Date; end: Date; type: ComegoTimeType; name?: string | null }>;
	delete: Array<{ id: string }>;
	unchanged: Array<{ id: string }>;
};

export function isDurationProjectTime(time: Time): boolean {
	const inputMode = (time as Time & { inputMode?: string }).inputMode;
	if (inputMode === "duration") return true;
	if (inputMode === "range") return false;
	if (!time.start || !time.end || !(time.timeDiff > 0)) return false;
	const start = new Date(time.start);
	const end = new Date(time.end);
	return start.getTime() === end.getTime() && start.getTime() === startOfDay(start).getTime();
}

function clampToDayEnd(date: Date, day: Date): Date {
	const dayEnd = endOfDay(startOfDay(day));
	return new Date(Math.min(date.getTime(), dayEnd.getTime()));
}

function laterOf(a: Date, b: Date): Date {
	return a.getTime() >= b.getTime() ? a : b;
}

function earlierOf(a: Date, b: Date): Date {
	return a.getTime() <= b.getTime() ? a : b;
}

/**
 * Earliest clock time work may start on `day` so the rest rule against the previous day holds.
 * A boundary outside the day is unusable: before it the schedule start already satisfies the rule,
 * after it no placement on this day could, and shifting anyway would empty the day.
 */
function usableRestStart(day: Date, earliestStart?: Date | null): Date | null {
	if (!earliestStart) return null;
	const dayStart = startOfDay(day);
	if (earliestStart.getTime() <= dayStart.getTime()) return null;
	if (earliestStart.getTime() >= endOfDay(dayStart).getTime()) return null;
	return earliestStart;
}

function clampIntervalToDay(interval: ProjectTimeInterval, day?: Date): ProjectTimeInterval {
	const dayStart = startOfDay(day ?? interval.start);
	return {
		start: interval.start.getTime() < dayStart.getTime() ? dayStart : interval.start,
		end: clampToDayEnd(interval.end, dayStart),
	};
}

/** Merges overlapping or abutting intervals into a sorted union (no overlap double-count). */
function mergeOverlappingIntervals(intervals: ProjectTimeInterval[]): ProjectTimeInterval[] {
	if (!intervals.length) return [];
	const sorted = [...intervals]
		.map((i) => ({ start: new Date(i.start), end: new Date(i.end) }))
		.filter((i) => i.end.getTime() > i.start.getTime())
		.sort((a, b) => a.start.getTime() - b.start.getTime());
	if (!sorted.length) return [];

	const merged: ProjectTimeInterval[] = [{ start: sorted[0].start, end: sorted[0].end }];
	for (let i = 1; i < sorted.length; i++) {
		const current = sorted[i];
		const last = merged[merged.length - 1];
		if (current.start.getTime() <= last.end.getTime()) {
			if (current.end.getTime() > last.end.getTime()) {
				last.end = current.end;
			}
		} else {
			merged.push({ start: current.start, end: current.end });
		}
	}
	return merged;
}

function normalizeRangedIntervals(times: Time[], day: Date): ProjectTimeInterval[] {
	const dayStart = startOfDay(day);
	const intervals: ProjectTimeInterval[] = [];
	for (const time of times) {
		if (isDurationProjectTime(time)) continue;
		if (!time?.start) continue;
		const start = new Date(time.start);
		const endSource = time.end ? new Date(time.end) : new Date();
		const end = clampToDayEnd(endSource, dayStart);
		if (end.getTime() <= start.getTime()) continue;
		intervals.push(clampIntervalToDay({ start, end }, dayStart));
	}
	return mergeOverlappingIntervals(intervals);
}

/** Sum duration-mode entries as milliseconds (timeDiff is seconds). */
function sumDurationModeMs(times: Time[]): number {
	let ms = 0;
	for (const time of times) {
		if (!isDurationProjectTime(time)) continue;
		const sec = time.timeDiff ?? 0;
		if (sec > 0) ms += sec * 1000;
	}
	return ms;
}

/**
 * Appends a duration block after the last ranged interval, or from dayStartFallback when none.
 * Clamps to end of day; returns updated work intervals (no overlaps).
 */
function appendDurationBlock(
	rangedUnion: ProjectTimeInterval[],
	durationMs: number,
	day: Date,
	dayStartFallback: Date
): ProjectTimeInterval[] {
	if (durationMs <= 0) return rangedUnion.map((i) => ({ start: new Date(i.start), end: new Date(i.end) }));
	const dayStart = startOfDay(day);
	const result = rangedUnion.map((i) => ({ start: new Date(i.start), end: new Date(i.end) }));

	let start: Date;
	if (result.length) {
		start = new Date(result[result.length - 1].end);
	} else {
		start = new Date(dayStartFallback);
	}

	const end = clampToDayEnd(addMilliseconds(start, durationMs), dayStart);
	if (end.getTime() <= start.getTime()) return result;

	if (result.length && start.getTime() === result[result.length - 1].end.getTime()) {
		result[result.length - 1].end = end;
	} else {
		result.push({ start, end });
	}
	return result;
}

function toDesiredEntries(intervals: ProjectTimeInterval[]): DesiredWorkingHourEntry[] {
	return intervals
		.filter((i) => i.end.getTime() > i.start.getTime())
		.map((i) => ({
			start: new Date(i.start),
			end: new Date(i.end),
			durationMs: i.end.getTime() - i.start.getTime(),
			type: "work" as ComegoTimeType,
		}));
}

export function buildDesiredWorkingHours(params: {
	day: Date;
	projectTimes: Time[];
	scheduleDayStart?: Date | null;
	/** Earliest start the rest rule against the previous day allows. */
	earliestStart?: Date | null;
}): DesiredWorkingHourEntry[] {
	const dayStart = startOfDay(params.day);
	const rangedUnion = normalizeRangedIntervals(params.projectTimes, dayStart);
	const durationMs = sumDurationModeMs(params.projectTimes);
	const scheduleStart =
		params.scheduleDayStart && params.scheduleDayStart.getTime() >= dayStart.getTime()
			? params.scheduleDayStart
			: addSeconds(dayStart, 9 * 3600);
	// Duration-mode project times carry no clock times, so this block start is invented here and may
	// be moved to the rest boundary. Ranged times keep mirroring their real clock times.
	const restStart = usableRestStart(dayStart, params.earliestStart);
	const fallbackStart = restStart ? laterOf(scheduleStart, restStart) : scheduleStart;
	const withDuration = appendDurationBlock(rangedUnion, durationMs, dayStart, fallbackStart);
	return toDesiredEntries(withDuration);
}

/** Overlap-free duration of a day's project times (ranged union + duration-mode entries). */
export function expectedNormalizedDurationMs(projectTimes: Time[], day: Date): number {
	const ranged = normalizeRangedIntervals(projectTimes, day);
	const rangedMs = ranged.reduce((sum, i) => sum + (i.end.getTime() - i.start.getTime()), 0);
	return rangedMs + sumDurationModeMs(projectTimes);
}

/**
 * Break figures the placement has to respect for a day totalling `totalMs` of work.
 * A break rule fires as soon as its threshold is passed, so continuous work has to stay under the
 * lowest one, while the credited pause has to cover the largest rule the day total reaches.
 */
function breakRuleFigures(
	breaks: BreakRule[] | null | undefined,
	minimumBreakDurationForCredit: number | null | undefined,
	totalMs: number
): { minCreditMs: number; maxContinuousMs: number; requiredBreakMs: number } {
	const rules = (breaks ?? []).filter((rule) => rule.workingHours > 0);
	return {
		minCreditMs: (minimumBreakDurationForCredit ?? 0.25) * HOUR_MS,
		maxContinuousMs: rules.length
			? Math.min(...rules.map((rule) => rule.workingHours * HOUR_MS))
			: Number.POSITIVE_INFINITY,
		requiredBreakMs: rules
			.filter((rule) => rule.workingHours * HOUR_MS < totalMs)
			.reduce((max, rule) => Math.max(max, rule.minutesDue * HOUR_MS), 0),
	};
}

/**
 * Fills `periods` with `durationMs` of work, inserting the breaks the rules demand. The last period
 * is extended up to `windowEndMs` when the duration outgrows the schedule, but the break goes to
 * that period's own end first: it is where the user's day was meant to stop, which keeps the
 * suggestion recognisable instead of cutting wherever the continuous-work limit happens to fall.
 * Rule state is read back from the placed blocks, so a gap between two schedule periods credits as
 * a break exactly like an inserted one. Work that no longer fits the window is dropped.
 */
function placeWorkWithBreaks(params: {
	periods: ProjectTimeInterval[];
	durationMs: number;
	windowEndMs: number;
	maxContinuousMs: number;
	requiredBreakMs: number;
	minCreditMs: number;
}): ProjectTimeInterval[] {
	const { periods, windowEndMs, maxContinuousMs, requiredBreakMs, minCreditMs } = params;
	const placed: ProjectTimeInterval[] = [];
	let remainingMs = params.durationMs;

	const missingBreakMs = () => Math.max(requiredBreakMs - creditedPauseMs(placed, minCreditMs), 0);
	const breakLengthMs = () => Math.max(missingBreakMs(), minCreditMs);

	const fill = (fromMs: number, hardEndMs: number): number => {
		let cursorMs = fromMs;
		for (let step = 0; step < MAX_PLACEMENT_STEPS && remainingMs > 0 && cursorMs < hardEndMs; step++) {
			const allowedMs = maxContinuousMs - continuousWorkMsBefore(placed, cursorMs, minCreditMs);
			if (allowedMs <= 0) {
				cursorMs += breakLengthMs();
				continue;
			}
			const endMs = Math.min(cursorMs + Math.min(remainingMs, allowedMs), hardEndMs);
			if (endMs <= cursorMs) break;
			placed.push({ start: new Date(cursorMs), end: new Date(endMs) });
			remainingMs -= endMs - cursorMs;
			cursorMs = endMs;
		}
		return cursorMs;
	};

	let cursorMs = Number.NEGATIVE_INFINITY;
	for (let i = 0; i < periods.length && remainingMs > 0; i++) {
		const period = periods[i];
		cursorMs = fill(Math.max(period.start.getTime(), cursorMs), Math.min(period.end.getTime(), windowEndMs));
		if (i < periods.length - 1 || remainingMs <= 0) continue;

		const continuousMs = continuousWorkMsBefore(placed, cursorMs, minCreditMs);
		if (missingBreakMs() > 0 || continuousMs + remainingMs > maxContinuousMs) {
			cursorMs += breakLengthMs();
		}
		cursorMs = fill(cursorMs, windowEndMs);
	}

	return mergeOverlappingIntervals(placed);
}

/**
 * Places the normalized project-time union duration into the day's schedule. Schedule gaps are
 * represented by separate work intervals and become pauses in the projected day. If the duration
 * exceeds the configured ranges, the last range is extended (up to end-of-day) behind the break the
 * rules demand at its end.
 *
 * Work beyond `dailyMaxHours` is dropped: a suggestion that still breaks the rule it is meant to
 * fix is worthless, so the suggested day stays shorter than the project times it mirrors. For the
 * same reason the rest rule against the adjacent days constrains placement: `earliestStart` shifts
 * the whole schedule later (preserving its block/gap shape and each block's length) when the normal
 * start would break the rule, and `latestEnd` caps the end of the last block.
 */
export function buildScheduleBasedWorkingHours(params: {
	day: Date;
	projectTimes: Time[];
	schedulePeriods?: ScheduleWorkPeriod[] | null;
	dailyMaxHours?: number | null;
	breaks?: BreakRule[] | null;
	minimumBreakDurationForCredit?: number | null;
	/** Earliest start the rest rule against the previous day allows. */
	earliestStart?: Date | null;
	/** Latest end the rest rule against the next day allows. */
	latestEnd?: Date | null;
}): DesiredWorkingHourEntry[] {
	const day = startOfDay(params.day);
	let durationMs = expectedNormalizedDurationMs(params.projectTimes, day);
	const dailyMaxMs = (params.dailyMaxHours ?? 0) * 3600 * 1000;
	if (dailyMaxMs > 0 && durationMs > dailyMaxMs) durationMs = dailyMaxMs;
	if (durationMs <= 0) return [];

	const toIntervals = (periods: ScheduleWorkPeriod[]): ProjectTimeInterval[] =>
		periods
			.map((period) => ({
				start: coerceTimeFormat(period.from, day),
				end: coerceTimeFormat(period.to, day),
			}))
			.filter(
				(period): period is ProjectTimeInterval =>
					!!period.start && !!period.end && period.end.getTime() > period.start.getTime()
			);
	const configured = toIntervals(params.schedulePeriods ?? []);
	const schedule = mergeOverlappingIntervals(
		configured.length ? configured : toIntervals(DEFAULT_SCHEDULE_WORK_PERIODS)
	);

	const dayEnd = endOfDay(day);
	const windowStart = usableRestStart(day, params.earliestStart);
	// The previous day is already persisted and cannot be moved, so when the two rest boundaries
	// cannot both hold the one against it wins and the next day's is dropped.
	const nextDayBoundary = params.latestEnd;
	const windowEnd =
		nextDayBoundary &&
		nextDayBoundary.getTime() < dayEnd.getTime() &&
		(!windowStart || nextDayBoundary.getTime() > windowStart.getTime())
			? nextDayBoundary
			: dayEnd;

	// Deviate the schedule start to the rest boundary when the normal start would break the rule,
	// shifting the whole schedule by the same amount so its block/gap shape and each block's length
	// are preserved. earliestStart earlier than the schedule start leaves it untouched (shift = 0).
	const scheduleFirstStart = schedule[0]?.start ?? addSeconds(day, 9 * 3600);
	const desiredStart = windowStart ? laterOf(scheduleFirstStart, windowStart) : scheduleFirstStart;
	const shiftMs = Math.max(0, desiredStart.getTime() - scheduleFirstStart.getTime());

	const shifted = schedule
		.map((period) => ({
			start: addMilliseconds(period.start, shiftMs),
			end: earlierOf(addMilliseconds(period.end, shiftMs), windowEnd),
		}))
		.filter((period) => period.start.getTime() < period.end.getTime());

	const { minCreditMs, maxContinuousMs, requiredBreakMs } = breakRuleFigures(
		params.breaks,
		params.minimumBreakDurationForCredit,
		durationMs
	);
	const place = (windows: ProjectTimeInterval[]) =>
		toDesiredEntries(
			placeWorkWithBreaks({
				periods: windows,
				durationMs,
				windowEndMs: windowEnd.getTime(),
				maxContinuousMs,
				requiredBreakMs,
				minCreditMs,
			})
		);

	// Nothing of the shifted schedule fits the window: place a single block at the boundary instead.
	if (!shifted.length) {
		// Placed backwards from the window end, the day has to leave room for the break too,
		// otherwise the work pushed behind it would run past the boundary and be dropped.
		const breakAllowanceMs =
			requiredBreakMs > 0 || durationMs > maxContinuousMs ? Math.max(requiredBreakMs, minCreditMs) : 0;
		const fallback =
			desiredStart.getTime() < windowEnd.getTime()
				? { start: desiredStart, end: windowEnd }
				: { start: laterOf(addMilliseconds(windowEnd, -(durationMs + breakAllowanceMs)), day), end: windowEnd };
		return place([fallback]);
	}

	return place(shifted);
}

/** Persisted work rows the project-time union still covers as a whole; they stay untouched. */
function keptWorkIntervals(existingDay: ComegoTime[], desired: ProjectTimeInterval[]): ProjectTimeInterval[] {
	return existingDay
		.filter((entry) => entry.type === "work" && !!entry.end)
		.map((entry) => ({ start: new Date(entry.start), end: new Date(entry.end!) }))
		.filter((interval) => interval.end.getTime() > interval.start.getTime())
		.filter((interval) =>
			desired.some(
				(period) =>
					period.start.getTime() <= interval.start.getTime() && period.end.getTime() >= interval.end.getTime()
			)
		)
		.sort((a, b) => a.start.getTime() - b.start.getTime());
}

/** Persisted pauses hold their slot: new work is placed around them, never on top of them. */
function existingPauseIntervals(existingDay: ComegoTime[]): ProjectTimeInterval[] {
	return existingDay
		.filter((entry) => entry.type === "pause" && !!entry.end)
		.map((entry) => ({ start: new Date(entry.start), end: new Date(entry.end!) }))
		.filter((interval) => interval.end.getTime() > interval.start.getTime());
}

/** Sum of the gaps between work blocks long enough to count as a break. */
function creditedPauseMs(work: ProjectTimeInterval[], minCreditMs: number): number {
	const merged = mergeOverlappingIntervals(work);
	let credited = 0;
	for (let i = 1; i < merged.length; i++) {
		const gap = merged[i].start.getTime() - merged[i - 1].end.getTime();
		if (gap >= minCreditMs) credited += gap;
	}
	return credited;
}

/** Uninterrupted work directly before `atMs`; a credited gap resets it, as evaluateWorkingHours does. */
function continuousWorkMsBefore(work: ProjectTimeInterval[], atMs: number, minCreditMs: number): number {
	const merged = mergeOverlappingIntervals(work).filter((interval) => interval.start.getTime() < atMs);
	let continuous = 0;
	let prevEndMs: number | null = null;
	for (const interval of merged) {
		const startMs = interval.start.getTime();
		const endMs = Math.min(interval.end.getTime(), atMs);
		if (prevEndMs !== null && startMs - prevEndMs >= minCreditMs) continuous = 0;
		continuous += endMs - startMs;
		prevEndMs = endMs;
	}
	if (prevEndMs !== null && atMs - prevEndMs >= minCreditMs) continuous = 0;
	return continuous;
}

/** First moment at or after `fromMs` that no work block occupies. */
function nextFreeStartMs(work: ProjectTimeInterval[], fromMs: number): number {
	let startMs = fromMs;
	for (const interval of mergeOverlappingIntervals(work)) {
		if (interval.start.getTime() <= startMs && interval.end.getTime() > startMs) {
			startMs = interval.end.getTime();
		}
	}
	return startMs;
}

/** Start of the next work block after `fromMs`, i.e. how far a new block may run. */
function nextBlockedStartMs(work: ProjectTimeInterval[], fromMs: number): number {
	let next = Number.POSITIVE_INFINITY;
	for (const interval of work) {
		const startMs = interval.start.getTime();
		if (startMs > fromMs && startMs < next) next = startMs;
	}
	return next;
}

/**
 * Pushes `startMs` later so the gap in front of it covers the break the rules still demand.
 * Only pauses before the insertion point count, so a break is never assumed to be covered by a
 * later gap that the placement is about to fill.
 */
function startAfterDueBreak(
	work: ProjectTimeInterval[],
	startMs: number,
	requiredBreakMs: number,
	minCreditMs: number
): number {
	if (requiredBreakMs <= 0) return startMs;
	const before = mergeOverlappingIntervals(work).filter((interval) => interval.start.getTime() < startMs);
	if (!before.length) return startMs;
	const missingMs = requiredBreakMs - creditedPauseMs(before, minCreditMs);
	if (missingMs <= 0) return startMs;
	const lastEndMs = before[before.length - 1].end.getTime();
	return Math.max(startMs, lastEndMs + Math.max(missingMs, minCreditMs, 1));
}

/**
 * Suggestion built on top of the day's persisted work: the rows the project-time union still
 * covers stay exactly where they are, and only the part the new project time adds is fitted around
 * them. Returns null when there is no such structure to build on, so the caller falls back to the
 * schedule-based reconstruction.
 *
 * The added work keeps its recorded clock times wherever the rules allow. It is only trimmed to the
 * remaining daily maximum, clamped into the rest window, and pushed later or split where a break is
 * due — so the result satisfies the rules by construction and needs no re-validation.
 */
export function buildIncrementalWorkingHours(params: {
	day: Date;
	desiredWork: DesiredWorkingHourEntry[];
	existingDay: ComegoTime[];
	dailyMaxHours?: number | null;
	breaks?: BreakRule[] | null;
	minimumBreakDurationForCredit?: number | null;
	/** Earliest start the rest rule against the previous day allows. */
	earliestStart?: Date | null;
	/** Latest end the rest rule against the next day allows. */
	latestEnd?: Date | null;
}): DesiredWorkingHourEntry[] | null {
	const day = startOfDay(params.day);
	const desired = mergeOverlappingIntervals(sortedDesiredWork(params.desiredWork));
	if (!desired.length) return null;

	const kept = keptWorkIntervals(params.existingDay, desired);
	if (!kept.length) return null;

	const keptMs = kept.reduce((sum, interval) => sum + (interval.end.getTime() - interval.start.getTime()), 0);
	const dailyMaxMs = (params.dailyMaxHours ?? 0) * HOUR_MS;
	let remainingMs = dailyMaxMs > 0 ? dailyMaxMs - keptMs : Number.POSITIVE_INFINITY;

	const added = desired
		.flatMap((interval) => subtractIntervals(interval, toCuts(kept)))
		.sort((a, b) => a.start.getTime() - b.start.getTime());
	if (!added.length || remainingMs <= 0) return toDesiredEntries(kept);

	const addedMs = added.reduce((sum, interval) => sum + (interval.end.getTime() - interval.start.getTime()), 0);
	const totalMs = keptMs + Math.min(addedMs, remainingMs);

	const { minCreditMs, maxContinuousMs, requiredBreakMs } = breakRuleFigures(
		params.breaks,
		params.minimumBreakDurationForCredit,
		totalMs
	);

	const windowStart = usableRestStart(day, params.earliestStart);
	const dayEndMs = endOfDay(day).getTime();
	const nextDayBoundary = params.latestEnd;
	const windowEndMs =
		nextDayBoundary &&
		nextDayBoundary.getTime() < dayEndMs &&
		(!windowStart || nextDayBoundary.getTime() > windowStart.getTime())
			? nextDayBoundary.getTime()
			: dayEndMs;

	// Rule state counts work only, so a persisted pause still credits as a break through the gap it
	// leaves; occupancy also counts those pauses, so work is never laid on top of one.
	const accepted = kept.map((interval) => ({ start: new Date(interval.start), end: new Date(interval.end) }));
	const blocked = [...accepted, ...existingPauseIntervals(params.existingDay)];
	const placed: ProjectTimeInterval[] = [];
	let cursorMs = windowStart ? windowStart.getTime() : day.getTime();

	for (const piece of added) {
		if (remainingMs <= 0) break;
		let pendingMs = Math.min(piece.end.getTime() - piece.start.getTime(), remainingMs);
		let startMs = Math.max(piece.start.getTime(), cursorMs);

		for (let step = 0; step < MAX_PLACEMENT_STEPS && pendingMs > 0; step++) {
			startMs = nextFreeStartMs(blocked, startMs);
			const afterBreakMs = startAfterDueBreak(accepted, startMs, requiredBreakMs, minCreditMs);
			if (afterBreakMs > startMs) {
				startMs = afterBreakMs;
				continue;
			}
			if (startMs >= windowEndMs) break;

			const allowedMs = maxContinuousMs - continuousWorkMsBefore(accepted, startMs, minCreditMs);
			if (allowedMs <= 0) {
				startMs += Math.max(minCreditMs, 1);
				continue;
			}

			const endMs = Math.min(
				startMs + Math.min(pendingMs, allowedMs),
				windowEndMs,
				nextBlockedStartMs(blocked, startMs)
			);
			if (endMs <= startMs) break;

			const block = { start: new Date(startMs), end: new Date(endMs) };
			accepted.push(block);
			blocked.push(block);
			placed.push(block);
			pendingMs -= endMs - startMs;
			remainingMs -= endMs - startMs;
			startMs = endMs;
		}

		cursorMs = Math.max(cursorMs, startMs);
	}

	// Work placed directly against a kept block belongs to the same stretch of work, so the pieces
	// are joined back into one row instead of being left as several touching ones.
	return toDesiredEntries(mergeOverlappingIntervals([...kept, ...placed]));
}

/** Gap pauses between sorted work entries (id-less). */
export function computeGapBreakEntries(workEntries: DesiredWorkingHourEntry[]): DesiredWorkingHourEntry[] {
	const sorted = [...workEntries].sort((a, b) => a.start.getTime() - b.start.getTime());
	const pauses: DesiredWorkingHourEntry[] = [];
	for (let i = 0; i < sorted.length - 1; i++) {
		const current = sorted[i];
		const next = sorted[i + 1];
		if (next.start.getTime() > current.end.getTime()) {
			pauses.push({
				start: new Date(current.end),
				end: new Date(next.start),
				durationMs: next.start.getTime() - current.end.getTime(),
				type: "pause",
			});
		}
	}
	return pauses;
}

/**
 * Builds a full day snapshot for validation: desired work + gap pauses,
 * reusing existing work/pause ids when matched (rows kept as-is first, then by start order).
 * IDs must be preserved so the rulebreak dialog does not treat store rows as overlaps, and so a
 * row the suggestion leaves untouched is still recognised as the persisted one.
 */
export function buildProjectedDayForValidation(
	desiredWork: DesiredWorkingHourEntry[],
	existingEntries: ComegoTime[]
): ComegoTime[] {
	const existingWork = [...existingEntries]
		.filter((e) => e.type === "work" && e.end && e.id)
		.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
	const wanted = [...desiredWork]
		.filter((e) => e.type === "work" && e.end.getTime() > e.start.getTime())
		.sort((a, b) => a.start.getTime() - b.start.getTime());

	const unassigned = [...existingWork];
	const matched: Array<ComegoTime | undefined> = wanted.map((w) => {
		const index = unassigned.findIndex(
			(e) => new Date(e.start).getTime() === w.start.getTime() && new Date(e.end!).getTime() === w.end.getTime()
		);
		return index >= 0 ? unassigned.splice(index, 1)[0] : undefined;
	});
	for (let i = 0; i < matched.length; i++) {
		if (!matched[i]) matched[i] = unassigned.shift();
	}

	const workRows: ComegoTime[] = wanted.map((w, i) => {
		const match = matched[i];
		if (match?.id) {
			return {
				...match,
				start: w.start.toISOString(),
				end: w.end.toISOString(),
				type: "work",
			} as ComegoTime;
		}
		return {
			name: null,
			start: w.start.toISOString(),
			end: w.end.toISOString(),
			type: "work",
		} as ComegoTime;
	});

	const existingPauses = existingEntries.filter((e) => e.type === "pause" && e.end);
	const desiredPauses = computeGapBreakEntries(desiredWork);
	const usedPauseIds = new Set<string>();
	const pauseRows: ComegoTime[] = [];

	for (const gap of desiredPauses) {
		const gapStartMs = gap.start.getTime();
		const gapEndMs = gap.end.getTime();
		const exact = existingPauses.find(
			(p) =>
				(!p.id || !usedPauseIds.has(p.id)) &&
				new Date(p.start).getTime() === gapStartMs &&
				new Date(p.end!).getTime() === gapEndMs
		);
		if (exact) {
			if (exact.id) usedPauseIds.add(exact.id);
			pauseRows.push(exact);
			continue;
		}
		const sameStart = existingPauses.find(
			(p) => p.id && !usedPauseIds.has(p.id) && new Date(p.start).getTime() === gapStartMs
		);
		if (sameStart) {
			usedPauseIds.add(sameStart.id!);
			pauseRows.push({ ...sameStart, end: gap.end.toISOString() } as ComegoTime);
			continue;
		}
		pauseRows.push({
			name: null,
			start: gap.start.toISOString(),
			end: gap.end.toISOString(),
			type: "pause",
		} as ComegoTime);
	}

	const absences = clipAbsencesAroundWork(existingEntries, wanted);
	return [...workRows, ...pauseRows, ...absences].sort(
		(a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
	);
}

/** Pause change set: delete unused existing pauses; add missing; update bounds when id kept. */
export function buildPauseChangeSet(
	existingEntries: ComegoTime[],
	projectedDay: ComegoTime[]
): Pick<WorkingHourChangeSet, "create" | "update" | "delete"> {
	const existingPauses = existingEntries.filter((e) => e.type === "pause" && e.id && e.end);
	const projectedPauses = projectedDay.filter((e) => e.type === "pause" && e.end);
	const projectedIds = new Set(projectedPauses.map((p) => p.id).filter(Boolean) as string[]);

	const create: WorkingHourChangeSet["create"] = [];
	const update: WorkingHourChangeSet["update"] = [];
	const sameMs = (a: string | Date, b: string | Date) => new Date(a).getTime() === new Date(b).getTime();

	for (const p of projectedPauses) {
		if (!p.id) {
			create.push({
				start: new Date(p.start),
				end: new Date(p.end!),
				durationMs: new Date(p.end!).getTime() - new Date(p.start).getTime(),
				type: "pause",
			});
			continue;
		}
		const orig = existingPauses.find((e) => e.id === p.id);
		if (!orig) continue;
		if (!sameMs(orig.start, p.start) || !sameMs(orig.end!, p.end!)) {
			update.push({
				id: p.id,
				start: new Date(p.start),
				end: new Date(p.end!),
				type: "pause",
				name: orig.name ?? null,
			});
		}
	}

	const del = existingPauses.filter((e) => e.id && !projectedIds.has(e.id)).map((e) => ({ id: e.id! }));
	return { create, update, delete: del };
}

export function changeSetHasWrites(cs: WorkingHourChangeSet): boolean {
	return cs.create.length > 0 || cs.update.length > 0 || cs.delete.length > 0;
}

const sameMs = (a: string | Date, b: string | Date) => new Date(a).getTime() === new Date(b).getTime();

function intervalsOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
	return aStart < bEnd && bStart < aEnd;
}

/** Removes every `cut` range from `interval`, returning the remaining pieces in order. */
function subtractIntervals(
	interval: ProjectTimeInterval,
	cuts: Array<{ start: number; end: number }>
): ProjectTimeInterval[] {
	let pieces: ProjectTimeInterval[] = [{ start: new Date(interval.start), end: new Date(interval.end) }];
	for (const cut of cuts) {
		const next: ProjectTimeInterval[] = [];
		for (const piece of pieces) {
			const ps = piece.start.getTime();
			const pe = piece.end.getTime();
			if (!intervalsOverlap(ps, pe, cut.start, cut.end)) {
				next.push(piece);
				continue;
			}
			if (ps < cut.start) next.push({ start: piece.start, end: new Date(cut.start) });
			if (pe > cut.end) next.push({ start: new Date(cut.end), end: piece.end });
		}
		pieces = next;
	}
	return pieces.filter((p) => p.end.getTime() > p.start.getTime());
}

function toCuts(intervals: Array<{ start: Date; end: Date }>): Array<{ start: number; end: number }> {
	return intervals
		.map((i) => ({ start: i.start.getTime(), end: i.end.getTime() }))
		.filter((i) => i.end > i.start)
		.sort((a, b) => a.start - b.start);
}

function sortedDesiredWork(desiredWork: DesiredWorkingHourEntry[]): DesiredWorkingHourEntry[] {
	return desiredWork
		.filter((e) => e.type === "work" && e.end.getTime() > e.start.getTime())
		.map((e) => ({ ...e, start: new Date(e.start), end: new Date(e.end) }))
		.sort((a, b) => a.start.getTime() - b.start.getTime());
}

/**
 * Absences never overlap work: tracked project time wins, so an absence is clipped to the
 * parts the work leaves free and dropped when fully covered. Clipped rows lose their id
 * because they have to be re-created; untouched rows keep it.
 */
function clipAbsencesAroundWork(existingEntries: ComegoTime[], work: Array<{ start: Date; end: Date }>): ComegoTime[] {
	const cuts = toCuts(work);
	const result: ComegoTime[] = [];
	for (const absence of existingEntries) {
		if (absence.type !== "absence") continue;
		if (!absence.end) {
			result.push({ ...absence });
			continue;
		}
		const pieces = subtractIntervals({ start: new Date(absence.start), end: new Date(absence.end) }, cuts);
		const untouched =
			pieces.length === 1 && sameMs(pieces[0].start, absence.start) && sameMs(pieces[0].end, absence.end);
		if (untouched) {
			result.push({ ...absence });
			continue;
		}
		for (const piece of pieces) {
			result.push({
				...absence,
				id: undefined,
				start: piece.start.toISOString(),
				end: piece.end.toISOString(),
			} as ComegoTime);
		}
	}
	return result;
}

/**
 * Range-scoped replace: keep closed work/pause rows that already match the desired timeline
 * exactly, and only delete-then-create the mismatches (plus absences that need clipping).
 *
 * No in-place updates — the API rejects any write that overlaps a still-persisted row, so the
 * delete batch always runs before creates. Open rows (no end) are never deleted.
 */
export function buildMinimalReplaceChangeSet(
	existingDay: ComegoTime[],
	desiredWork: DesiredWorkingHourEntry[]
): WorkingHourChangeSet {
	const work = sortedDesiredWork(desiredWork);
	const wanted = [...work, ...computeGapBreakEntries(work)].sort((a, b) => a.start.getTime() - b.start.getTime());

	const existingClosed = existingDay
		.filter((e) => (e.type === "work" || e.type === "pause") && !!e.id && !!e.end)
		.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

	const matchedExistingIds = new Set<string>();
	const matchedWantedIndexes = new Set<number>();
	const unchanged: WorkingHourChangeSet["unchanged"] = [];

	for (let i = 0; i < wanted.length; i++) {
		const d = wanted[i];
		const match = existingClosed.find(
			(e) =>
				!matchedExistingIds.has(e.id!) && e.type === d.type && sameMs(e.start, d.start) && sameMs(e.end!, d.end)
		);
		if (!match?.id) continue;
		matchedExistingIds.add(match.id);
		matchedWantedIndexes.add(i);
		unchanged.push({ id: match.id });
	}

	const create: DesiredWorkingHourEntry[] = wanted.filter((_, i) => !matchedWantedIndexes.has(i));
	const del: WorkingHourChangeSet["delete"] = existingClosed
		.filter((e) => e.id && !matchedExistingIds.has(e.id))
		.map((e) => ({ id: e.id! }));

	// Absences: only rewrite ones the new work forces a clip on; untouched keep their row.
	const absences = clipAbsencesAroundWork(existingDay, work);
	const untouchedAbsenceIds = new Set(absences.filter((a) => !!a.id).map((a) => a.id!));
	for (const absence of absences) {
		if (absence.id || !absence.end) continue;
		const start = new Date(absence.start);
		const end = new Date(absence.end);
		const typeId = (absence as ComegoTime & { typeId?: string }).typeId;
		create.push({
			start,
			end,
			durationMs: end.getTime() - start.getTime(),
			type: "absence",
			source: { name: absence.name ?? null, ...(typeId ? { typeId } : {}) },
		});
	}
	for (const entry of existingDay) {
		if (entry.type !== "absence" || !entry.id || !entry.end) continue;
		if (untouchedAbsenceIds.has(entry.id)) continue;
		del.push({ id: entry.id });
	}

	create.sort((a, b) => a.start.getTime() - b.start.getTime());
	return { create, update: [], delete: del, unchanged };
}

/**
 * True when the persisted day already is what the replace would write. Without this guard every
 * project-time edit would delete and re-create the whole day, including the debounced blur
 * updates the inline row editor fires.
 */
export function dayEqualsDesired(existingDay: ComegoTime[], desiredWork: DesiredWorkingHourEntry[]): boolean {
	const work = sortedDesiredWork(desiredWork);
	const expected = [...work, ...computeGapBreakEntries(work)].sort((a, b) => a.start.getTime() - b.start.getTime());
	const actual = existingDay
		.filter((e) => (e.type === "work" || e.type === "pause") && e.end)
		.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
	if (actual.length !== expected.length) return false;

	for (let i = 0; i < expected.length; i++) {
		if (actual[i].type !== expected[i].type) return false;
		if (!sameMs(actual[i].start, expected[i].start)) return false;
		if (!sameMs(actual[i].end!, expected[i].end)) return false;
	}

	// An absence still overlapping the work has to be clipped, so the day is not settled yet.
	const cuts = toCuts(work);
	return !existingDay.some(
		(e) =>
			e.type === "absence" &&
			!!e.end &&
			cuts.some((cut) =>
				intervalsOverlap(new Date(e.start).getTime(), new Date(e.end!).getTime(), cut.start, cut.end)
			)
	);
}
